<script>
  import { onMount } from 'svelte';
  import PasswordChange from '../components/PasswordChange.svelte';
  import SafeModeBanner from '../components/SafeModeBanner.svelte';
  import {
    getApps, toggleApp, changePassword,
    activateSafeMode, deactivateSafeMode, getSystemStatus
  } from '../services/api';

  let apps = [];
  let loading = true;
  let safeModeData = { active: false, cpu: 0, memory: 0 };
  let passwordComponent;

  onMount(async () => {
    await refresh();
  });

  async function refresh() {
    try {
      const [appsResult, statusResult] = await Promise.all([getApps(), getSystemStatus()]);
      if (appsResult.success) apps = appsResult.apps;
      if (statusResult.success) safeModeData = statusResult.system.safeMode;
    } catch (err) {
      console.error('Settings refresh error:', err);
    } finally {
      loading = false;
    }
  }

  async function handleToggle(app) {
    if (app.locked) return;
    const newEnabled = !app.enabled;
    try {
      const result = await toggleApp(app.id, newEnabled);
      if (result.success) {
        apps = apps.map(a => a.id === app.id ? { ...a, enabled: newEnabled } : a);
      }
    } catch (err) {
      console.error('Toggle error:', err);
    }
  }

  async function handlePasswordChange(e) {
    const { oldPassword, newPassword, confirmPassword } = e.detail;
    try {
      const result = await changePassword(oldPassword, newPassword, confirmPassword);
      if (result.success) {
        passwordComponent.setSuccess(result.message);
      } else {
        passwordComponent.setError(result.error || 'Failed to change password');
      }
    } catch (err) {
      passwordComponent.setError('Network error — try again');
    }
  }

  async function handleActivateSafeMode() {
    try {
      const result = await activateSafeMode();
      if (result.success) {
        safeModeData = { ...safeModeData, active: true };
        await refresh();
      }
    } catch (err) {
      console.error('Safe mode activate error:', err);
    }
  }

  async function handleDeactivateSafeMode() {
    try {
      const result = await deactivateSafeMode();
      if (result.success) {
        safeModeData = { ...safeModeData, active: false };
        await refresh();
      }
    } catch (err) {
      console.error('Safe mode deactivate error:', err);
    }
  }
</script>

<div class="settings-page">
  <h1 class="section-title">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9"/>
    </svg>
    Settings
  </h1>

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <span>Loading settings...</span>
    </div>
  {:else}
    <!-- Safe Mode Banner -->
    <SafeModeBanner
      active={safeModeData.active}
      cpu={safeModeData.cpu}
      memory={safeModeData.memory}
      on:deactivate={handleDeactivateSafeMode}
    />

    <div class="settings-grid">
      <!-- App Activation Control -->
      <div class="card app-control-card" id="app-activation-card">
        <div class="card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/>
          </svg>
          App Activation Control
        </div>

        <div class="app-toggles">
          {#each apps as app}
            <div class="app-toggle-row" class:disabled-app={!app.enabled} class:locked={app.locked}>
              <div class="toggle-app-info">
                <div class="toggle-color" style="background: {app.color}"></div>
                <span class="toggle-app-name">{app.name}</span>
                {#if app.locked}
                  <span class="locked-badge">Locked</span>
                {/if}
              </div>
              <label class="toggle-wrap" class:locked={app.locked}>
                <input
                  class="toggle-input"
                  type="checkbox"
                  checked={app.enabled}
                  disabled={app.locked}
                  on:change={() => handleToggle(app)}
                />
                <span class="toggle-slider"></span>
              </label>
            </div>
          {/each}
        </div>
      </div>

      <!-- Password Change -->
      <PasswordChange bind:this={passwordComponent} on:change={handlePasswordChange} />

      <!-- Safe Mode Control -->
      <div class="card safe-mode-control" id="safe-mode-control">
        <div class="card-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          Safe Mode
        </div>
        <p class="safe-mode-desc">
          Safe Mode disables non-critical apps to prevent system overload.
          It can be activated manually or automatically when CPU exceeds 90% for 5 minutes or memory exceeds 85%.
        </p>
        <div class="safe-mode-actions">
          {#if safeModeData.active}
            <button class="btn-success" on:click={handleDeactivateSafeMode} id="deactivate-safe-mode-btn">
              Deactivate Safe Mode
            </button>
          {:else}
            <button class="btn-danger" on:click={handleActivateSafeMode} id="activate-safe-mode-btn">
              Activate Safe Mode
            </button>
          {/if}
          <span class="safe-mode-status" class:active={safeModeData.active}>
            {safeModeData.active ? '⚠️ ACTIVE' : '✓ Inactive'}
          </span>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .settings-page {
    animation: fadeIn 0.3s ease;
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 60px;
    color: var(--text-secondary);
    font-size: 14px;
  }

  .spinner {
    width: 28px;
    height: 28px;
    border: 2px solid var(--border-default);
    border-top-color: var(--neon-blue);
    border-radius: 50%;
    animation: spinSlow 0.8s linear infinite;
  }

  .settings-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  /* App Activation */
  .app-control-card .card-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .app-control-card .card-title svg {
    color: var(--neon-green);
  }

  .app-toggles {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .app-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: var(--radius-sm);
    transition: background 0.2s ease;
  }

  .app-toggle-row:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  .app-toggle-row.disabled-app {
    opacity: 0.5;
  }

  .app-toggle-row.locked {
    opacity: 0.7;
  }

  .toggle-app-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .toggle-color {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .toggle-app-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }

  .locked-badge {
    font-size: 10px;
    font-weight: 600;
    color: var(--text-tertiary);
    background: rgba(255, 255, 255, 0.05);
    padding: 2px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .toggle-wrap.locked {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /* Safe Mode Control */
  .safe-mode-control .card-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .safe-mode-control .card-title svg {
    color: var(--neon-orange);
  }

  .safe-mode-desc {
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 16px;
  }

  .safe-mode-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .safe-mode-status {
    font-size: 13px;
    font-weight: 600;
    color: var(--neon-green);
  }

  .safe-mode-status.active {
    color: var(--neon-orange);
  }

  @media (max-width: 1024px) {
    .settings-grid { grid-template-columns: 1fr; }
  }
</style>
